/**
 * W-041 — Report detail. Stories: US-A07.
 *
 * AC2 the full content, its author and the thread context
 * AC3 dismiss · hide · delete · warn · ban
 * AC4 hidden content disappears from the app immediately (server-side, via RLS
 *     on `posts_read` / `replies_read`, which already exclude `is_hidden`)
 * AC5 a ban carries a duration or is permanent, and a reason  → WD-4
 * AC6 a resolved report leaves the queue and is written to the audit log
 *
 * A report's target has no foreign key (`target_id` is polymorphic), so the
 * post or reply is fetched as a second query keyed off `target_type` rather
 * than embedded on the report itself — the same two-tier shape as
 * `pages/users/show.tsx` (a thin page for the loading state, a content
 * component once the record is known), for the same reason: the follow-up
 * queries must not run before the report id is.
 */
import { useState } from 'react'
import { PageHeader } from '@refinedev/antd'
import { CanAccess, useInvalidate, useList, useOne, useShow } from '@refinedev/core'
import { Alert, Button, Card, Descriptions, Space, Spin, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import { useNavigate, useParams } from 'react-router'
import { StatusTag } from '@/components'
import { BanUserDialog, ModerationActionDialog, WarnUserDialog } from '@/components/dialogs'
import type { ModerationAction } from '@/components/dialogs/ModerationActionDialog'
import { invokeEdgeFunction } from '@/lib/supabase'
import { useAdminTranslate } from '@/providers/i18n-provider'
import type { Post, Reply, Report } from '@/types'

type Author = { id: string; full_name: string } | null
type PostWithJoins = Post & { profiles: Author; subjects: { name_fr: string } | null }
type ReplyWithJoins = Reply & { profiles: Author; posts: { id: string; body: string } | null }

export const ReportShowPage = () => {
  const translate = useAdminTranslate()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { query } = useShow<Report>({ resource: 'reports', id })
  const report = query.data?.data

  if (!report) {
    return (
      <PageHeader title={translate('admin.pages.report')} onBack={() => navigate('/moderation')}>
        <Spin spinning />
      </PageHeader>
    )
  }

  return <ReportShowContent report={report} onBack={() => navigate('/moderation')} />
}

const ReportShowContent = ({ report, onBack }: { report: Report; onBack: () => void }) => {
  const translate = useAdminTranslate()
  const navigate = useNavigate()
  const invalidate = useInvalidate()

  const { data: siblingData } = useList<Report>({
    resource: 'reports',
    filters: [
      { field: 'target_type', operator: 'eq', value: report.target_type },
      { field: 'target_id', operator: 'eq', value: report.target_id },
      { field: 'status', operator: 'eq', value: 'open' },
    ],
    pagination: { mode: 'off' },
  })
  const openReports = siblingData?.data ?? []

  const isPost = report.target_type === 'post'

  const { data: postData, isLoading: postLoading } = useOne<PostWithJoins>({
    resource: 'posts',
    id: report.target_id,
    meta: { select: '*, profiles(id, full_name), subjects(name_fr)' },
    queryOptions: { enabled: isPost },
  })
  const { data: replyData, isLoading: replyLoading } = useOne<ReplyWithJoins>({
    resource: 'replies',
    id: report.target_id,
    meta: { select: '*, profiles(id, full_name), posts(id, body)' },
    queryOptions: { enabled: !isPost },
  })

  const post = isPost ? postData?.data : undefined
  const reply = !isPost ? replyData?.data : undefined
  const contentLoading = isPost ? postLoading : replyLoading
  const author = post?.profiles ?? reply?.profiles ?? null
  const isHidden = Boolean(post?.is_hidden ?? reply?.is_hidden)
  const isDeleted = Boolean(post?.is_deleted ?? reply?.is_deleted)

  const [moderationAction, setModerationAction] = useState<ModerationAction | null>(null)
  const [warnOpen, setWarnOpen] = useState(false)
  const [banOpen, setBanOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function refresh() {
    void invalidate({ resource: 'reports', invalidates: ['list', 'detail'] })
  }

  async function runModerationAction(action: ModerationAction, reason: string) {
    await invokeEdgeFunction('moderation-content-action', {
      report_ids: openReports.length > 0 ? openReports.map(r => r.id) : [report.id],
      target_type: report.target_type,
      target_id: report.target_id,
      action,
      reason,
    })
    setModerationAction(null)
    if (action === 'dismiss') {
      navigate('/moderation')
      return
    }
    refresh()
  }

  return (
    <PageHeader title={translate('admin.pages.report')} onBack={onBack}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {error ? (
          <Alert type="error" showIcon message={error} closable onClose={() => setError(null)} />
        ) : null}

        <Card size="small" title={translate('admin.pages.report')}>
          <Descriptions column={{ xs: 1, md: 2 }} size="small">
            <Descriptions.Item label={translate('admin.screens.moderation.reportedAt')}>
              {dayjs(report.created_at).format('DD/MM/YYYY HH:mm')}
            </Descriptions.Item>
            <Descriptions.Item label={translate('admin.columns.status')}>
              <StatusTag kind="report" value={report.status} />
            </Descriptions.Item>
            <Descriptions.Item label={translate('admin.common.type')}>
              {translate(`admin.screens.moderation.target${isPost ? 'Post' : 'Reply'}`)}
            </Descriptions.Item>
            <Descriptions.Item label={translate('admin.screens.moderation.reportCount')}>
              {openReports.length || 1}
            </Descriptions.Item>
            <Descriptions.Item label={translate('admin.screens.moderation.reportReason')} span={2}>
              {report.reason}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card
          size="small"
          title={translate('admin.screens.moderation.contentPreview')}
          loading={contentLoading}
        >
          {post || reply ? (
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <Space wrap>
                <Typography.Text strong>{author?.full_name ?? '—'}</Typography.Text>
                <Typography.Text type="secondary">
                  {dayjs((post ?? reply)?.created_at).format('DD/MM/YYYY HH:mm')}
                </Typography.Text>
                {isDeleted ? (
                  <Tag color="error">{translate('admin.common.deactivated')}</Tag>
                ) : null}
                <Tag color={isHidden ? 'warning' : 'success'}>
                  {translate(
                    isHidden
                      ? 'admin.screens.moderation.contentHidden'
                      : 'admin.screens.moderation.contentVisible',
                  )}
                </Tag>
              </Space>

              {!isPost && reply?.posts ? (
                <Typography.Paragraph type="secondary" italic>
                  {translate('admin.screens.moderation.parentPost')}: {reply.posts.body}
                </Typography.Paragraph>
              ) : null}

              <Typography.Paragraph>{(post ?? reply)?.body}</Typography.Paragraph>
            </Space>
          ) : (
            <Typography.Text type="secondary">
              {translate('admin.screens.moderation.reportEmpty')}
            </Typography.Text>
          )}
        </Card>

        <CanAccess resource="reports" action="moderate">
          <Card size="small">
            <Space wrap>
              <Button onClick={() => setModerationAction('dismiss')}>
                {translate('admin.screens.moderation.dismiss')}
              </Button>
              {isHidden ? (
                <Button onClick={() => setModerationAction('unhide')}>
                  {translate('admin.screens.moderation.unhide')}
                </Button>
              ) : (
                <Button danger onClick={() => setModerationAction('hide')}>
                  {translate('admin.screens.moderation.hide')}
                </Button>
              )}
              <Button danger disabled={isDeleted} onClick={() => setModerationAction('delete')}>
                {translate('admin.screens.moderation.delete')}
              </Button>
              <Button disabled={!author} onClick={() => setWarnOpen(true)}>
                {translate('admin.screens.moderation.warn')}
              </Button>
              <Button danger disabled={!author} onClick={() => setBanOpen(true)}>
                {translate('admin.screens.moderation.ban')}
              </Button>
            </Space>
          </Card>
        </CanAccess>
      </Space>

      <ModerationActionDialog
        open={moderationAction !== null}
        action={moderationAction ?? 'dismiss'}
        onConfirm={async reason => {
          if (!moderationAction) return
          try {
            await runModerationAction(moderationAction, reason)
          } catch {
            setError(translate('admin.screens.moderation.actionFailed'))
            setModerationAction(null)
          }
        }}
        onCancel={() => setModerationAction(null)}
      />

      <WarnUserDialog
        open={warnOpen}
        userName={author?.full_name ?? ''}
        onConfirm={async ({ message, reason }) => {
          if (!author) return
          await invokeEdgeFunction('moderation-warn', { user_id: author.id, message, reason })
          setWarnOpen(false)
        }}
        onCancel={() => setWarnOpen(false)}
      />

      <BanUserDialog
        open={banOpen}
        userName={author?.full_name ?? ''}
        onConfirm={async ({ duration, reason }) => {
          if (!author) return
          // moderation-ban writes its own audit entry atomically with the ban.
          await invokeEdgeFunction('moderation-ban', { user_id: author.id, duration, reason })
          setBanOpen(false)
        }}
        onCancel={() => setBanOpen(false)}
      />
    </PageHeader>
  )
}
